import React from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEOHead from "../components/SEO/SEOHead";
import Breadcrumbs from "../components/SEO/Breadcrumbs";
import { getWebSiteSchema, getTouristAttractionSchema } from "../components/SEO/schemaTemplates";
import "./HotelsHubPage.css";

export default function FamousPlacesPage() {
  const { placeSlug } = useParams();

  const places = [
    {
      title: "Sigiriya Ancient Rock Fortress (Lion Rock)",
      slug: "sigiriya-ancient-rock-fortress",
      summary: "A 5th-century royal palace citadel perched on a 200m vertical rock pillar. Renowned for ancient fresco paintings, the Mirror Wall, and landscaped water gardens.",
      city: "Sigiriya",
      tag: "UNESCO World Heritage",
      bestTime: "Jan - Apr, Jul - Sep",
      lat: 7.9570,
      lng: 80.7603
    },
    {
      title: "Nine Arch Bridge (Demodara Bridge in the Sky)",
      slug: "nine-arch-bridge-ella",
      summary: "A massive colonial-era stone railway viaduct hidden inside lush green tea estates. Best spot to watch the famous blue hill country train.",
      city: "Ella",
      tag: "Iconic Landmark",
      bestTime: "Year-Round (Best mornings)",
      lat: 6.8768,
      lng: 81.0608
    },
    {
      title: "Yala National Park Leopard & Elephant Safari",
      slug: "yala-national-park-leopard-safari",
      summary: "World's highest concentration of wild leopards, alongside herds of Asian elephants, sloth bears, spotted deer, and vibrant birdlife.",
      city: "Yala",
      tag: "Wildlife Safari",
      bestTime: "Feb - Jun",
      lat: 6.3725,
      lng: 81.5173
    },
    {
      title: "Temple of the Sacred Tooth Relic (Sri Dalada Maligawa)",
      slug: "temple-of-the-sacred-tooth-relic-kandy",
      summary: "Sri Lanka's most venerated Buddhist temple located in the royal hill capital of Kandy, housing the left canine tooth relic of Lord Buddha.",
      city: "Kandy",
      tag: "Sacred Cultural Site",
      bestTime: "Year-Round",
      lat: 7.2936,
      lng: 80.6413
    },
    {
      title: "Galle Dutch Fort & Ocean Lighthouse",
      slug: "galle-dutch-fort-unesco-world-heritage",
      summary: "A 16th-century fortified colonial seaside city featuring cobbled streets, Dutch-era mansions, art galleries, and historic sunset bastions.",
      city: "Galle",
      tag: "Living UNESCO Heritage",
      bestTime: "Nov - Apr",
      lat: 6.0269,
      lng: 80.2170
    },
    {
      title: "Mirissa Beach & Whale Watching Coast",
      slug: "mirissa-whale-watching-coast",
      summary: "Crescent-shaped tropical bay famous for Blue Whale watching expeditions, Coconut Tree Hill sunsets, and vibrant beachfront dining.",
      city: "Mirissa",
      tag: "Coastal & Marine",
      bestTime: "Nov - Apr",
      lat: 5.9482,
      lng: 80.4578
    }
  ];

  const currentPlace = placeSlug ? places.find((p) => p.slug === placeSlug.toLowerCase()) : null;

  const breadcrumbItems = currentPlace
    ? [
        { name: "Most Famous Places", url: "/most-famous-places-sri-lanka" },
        { name: currentPlace.title, url: `/most-famous-places-sri-lanka/${currentPlace.slug}` }
      ]
    : [{ name: "Most Famous Places Sri Lanka", url: "/most-famous-places-sri-lanka" }];

  const attractionsSchemas = currentPlace
    ? [
        getTouristAttractionSchema({
          id: currentPlace.slug,
          name: currentPlace.title,
          description: currentPlace.summary,
          locationName: `${currentPlace.city}, Sri Lanka`,
          latitude: currentPlace.lat,
          longitude: currentPlace.lng
        })
      ]
    : places.map((p) =>
        getTouristAttractionSchema({
          id: p.slug,
          name: p.title,
          description: p.summary,
          locationName: `${p.city}, Sri Lanka`,
          latitude: p.lat,
          longitude: p.lng
        })
      );

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [getWebSiteSchema(), ...attractionsSchemas]
  };

  const pageTitle = currentPlace
    ? `${currentPlace.title} Travel Guide | Soloway Sri Lanka`
    : "Most Famous Places in Sri Lanka to Visit (2026 Guide) | Soloway";

  const pageDescription = currentPlace
    ? `${currentPlace.summary} Plan your visit to ${currentPlace.city}. Best time: ${currentPlace.bestTime}. Find nearby vetted stays and certified guides on Soloway.`
    : "Comprehensive travel guide to the most famous places in Sri Lanka. Explore Sigiriya Lion Rock, Ella Nine Arch Bridge, Yala Safari, and Galle Fort with local tips.";

  const canonicalUrl = currentPlace
    ? `/most-famous-places-sri-lanka/${currentPlace.slug}`
    : "/most-famous-places-sri-lanka";

  return (
    <div className="seo-page-wrapper">
      <SEOHead 
        title={pageTitle}
        description={pageDescription}
        keywords="most famous places sri lanka, places to visit in sri lanka, top attractions sri lanka, sigiriya lion rock, ella nine arch bridge, yala safari park, kandy tooth temple"
        canonicalUrl={canonicalUrl}
        structuredData={combinedSchema}
      />
      <Navbar />

      <main className="seo-page-container">
        <div className="content-inner">
          <Breadcrumbs items={breadcrumbItems} />

          <header className="page-header-seo">
            <span className="seo-badge">Sri Lanka Travel Guide & Attractions</span>
            <h1>
              {currentPlace
                ? currentPlace.title
                : "Most Famous Places in Sri Lanka You Must Visit"}
            </h1>
            <p className="lead-text">
              {currentPlace
                ? currentPlace.summary
                : "From ancient rock fortresses in the cultural triangle to mist-shrouded mountain peaks and tropical wildlife safaris, discover Sri Lanka's top bucket-list landmarks."}
            </p>
          </header>

          <section className="destinations-grid-section" aria-labelledby="places-heading">
            <div className="section-title-row">
              <h2 id="places-heading">Top Ranked Sri Lankan Attractions</h2>
              <span className="total-stays-count">6 Top Landmarks</span>
            </div>

            <div className="destinations-grid">
              {places.map((place, idx) => (
                <article key={place.slug} className="city-card">
                  <div className="city-card-header">
                    <span className="city-price-tag">Rank #{idx + 1}</span>
                  </div>
                  <span className="listing-count" style={{ color: "#0d9488", fontWeight: 600, marginBottom: "0.25rem" }}>
                    {place.tag} &bull; {place.city}
                  </span>
                  <h3>
                    <Link to={`/most-famous-places-sri-lanka/${place.slug}`} className="city-card-title-link">
                      {place.title}
                    </Link>
                  </h3>
                  <p>{place.summary}</p>
                  <p style={{ fontSize: "0.8125rem", color: "#64748b", margin: "0 0 0.75rem" }}>
                    <strong>Best Time to Visit:</strong> {place.bestTime}
                  </p>
                  <div className="city-card-footer">
                    <Link to={`/hotels/${place.city.toLowerCase()}`} className="view-link" style={{ fontSize: "0.8125rem" }}>
                      Hotels in {place.city} &rarr;
                    </Link>
                    <Link to={`/guides?search=${place.city.toLowerCase()}`} className="view-link" style={{ fontSize: "0.8125rem" }}>
                      Find Guide &rarr;
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <article className="seo-rich-content">
            <h2>Planning Your Sri Lanka Travel Itinerary</h2>
            <p>
              To experience the <strong>most famous places in Sri Lanka</strong> seamlessly, an 8-to-12 day loop is recommended:
            </p>
            <ol style={{ paddingLeft: "1.25rem", marginBottom: "1rem" }}>
              <li><strong>Cultural Triangle (Days 1–3):</strong> Climb Sigiriya Lion Rock and visit the ancient cave temples of Dambulla.</li>
              <li><strong>Hill Country & Tea Estates (Days 4–7):</strong> Take the scenic train ride to Kandy and Ella; hike Nine Arch Bridge and Little Adam’s Peak.</li>
              <li><strong>Wildlife & Coast (Days 8–11):</strong> Experience an early morning Yala Safari before heading down to Mirissa and the colonial ramparts of Galle Fort.</li>
            </ol>
            <p>
              Book certified local drivers and hotels directly on Soloway for the best island journey.
            </p>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
